import React from "react";
import { MapPin, ShieldCheck, UserCheck2Icon } from "lucide-react";

const InfoItem = ({ label, value, valueClass = "", breakWord = false }) => {
  return (
    <div className="border-b border-gray-100 pb-3">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
        {label}
      </p>

      <p
        className={`text-sm font-medium text-gray-800 ${
          breakWord ? "break-all" : ""
        } ${valueClass}`}
      >
        {value || "-"}
      </p>
    </div>
  );
};

const AadhaarKycData = ({ data, fulldata }) => {
  if (!data) return null;

  // Extract inner data whether passed as response.data or response directly
  const person = data?.data || data;
  const poa = person?.address || {};
  const photo = person?.profile_image;

  return (
    <div className="px-3 md:px-5 mt-4 mb-8 space-y-8">
      <div className="bg-white border border-gray-200 rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 px-5 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                  {person?.full_name || "KYC Verification"}
                </h2>

                <span className="text-xs border border-green-200 bg-green-50 text-green-700 px-2 py-1 rounded-md font-medium">
                  {fulldata?.status?.type === "success" || person?.status === "success_aadhaar"
                    ? "Verified"
                    : "Pending"}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {fulldata?.message || "Aadhaar Verification Successful"}
              </p>
            </div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 p-4 md:p-6">
          {/* LEFT SIDE DETAILS */}
          <div className="xl:col-span-7 space-y-6">
            {/* Personal Details */}
            <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-slate-800">
                  Personal Details
                </h3>

                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-lg text-blue-600">
                  <UserCheck2Icon />
                </div>
              </div>

              {/* Profile Image */}
              {photo && (
                <div className="flex justify-center md:justify-start mb-6">
                  <img
                    src={`data:image/jpeg;base64,${photo.replace(/\s/g, "")}`}
                    alt="Profile"
                    className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-xl"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InfoItem label="Full Name" value={person?.full_name} />

                <InfoItem label="Date of Birth" value={person?.dob} />

                <InfoItem
                  label="Gender"
                  value={
                    person?.gender === "M"
                      ? "Male"
                      : person?.gender === "F"
                      ? "Female"
                      : person?.gender
                  }
                />

                <InfoItem
                  label="Aadhaar Number"
                  value={person?.aadhaar_number ? "XXXX-XXXX-XXXX" : "-"}
                />
              </div>
            </div>

            {/* Address Details */}
            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-5 md:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-slate-800">
                  Address Details
                </h3>

                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-lg text-emerald-600">
                  <MapPin />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InfoItem label="Care Of" value={person?.care_of} />

                <InfoItem label="House" value={poa?.house} />

                <InfoItem label="Street / Locality" value={poa?.street || poa?.loc} />

                <InfoItem label="Post Office" value={poa?.po} />

                <InfoItem label="Village / Town" value={poa?.vtc} />

                <InfoItem label="Sub District" value={poa?.subdist} />

                <InfoItem label="District" value={poa?.dist} />

                <InfoItem label="State" value={poa?.state} />

                <InfoItem label="PIN Code" value={person?.zip} />

                <InfoItem label="Country" value={poa?.country} />
              </div>
            </div>

            {/* Verification Metadata */}
            <div className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-2xl p-5 md:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-slate-800">
                  Verification Metadata
                </h3>

                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center text-lg text-violet-600">
                  <ShieldCheck />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InfoItem
                  label="Mobile Verified"
                  value={person?.mobile_verified ? "Yes" : "No"}
                  valueClass={
                    person?.mobile_verified
                      ? "text-emerald-600 font-semibold"
                      : "text-amber-600 font-semibold"
                  }
                />

                <InfoItem
                  label="Face Score / Status"
                  value={person?.face_status ? "Matched" : "Not Matched / N/A"}
                />

                <InfoItem label="Share Code" value={person?.share_code} />

                <InfoItem label="Status Type" value={person?.status} />
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - JSON VIEWER */}
          <div className="xl:col-span-5">
            <div className="bg-slate-950 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden sticky top-5">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                <div>
                  <h3 className="text-white font-semibold text-lg">
                    Raw JSON Data
                  </h3>
                  <p className="text-slate-400 text-xs mt-1">
                    Complete API response
                  </p>
                </div>

                <div className="flex gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
              </div>

              <div className="max-h-[800px] overflow-auto">
                <pre className="text-[12px] leading-6 text-green-300 p-5 whitespace-pre-wrap break-words">
                  {JSON.stringify(fulldata || data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AadhaarKycData;